/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from '../../integrations/supabase/client';
import { SlaMonitoringService } from '../../services/slaMonitoringService';
import { 
  getMissingRoles, 
  type OnboardingRoleStatus, 
  type OnboardingCompletionContext 
} from '../../utils/onboardingCompletionUtils';
import type {
  OnboardingFlow,
  StalledOnboardingFlow,
  DetailedOnboardingFlow,
  OnboardingInvitation,
  OnboardingStep,
  OnboardingActivity,
  IOnboardingRepository,
} from '../interfaces/IOnboardingRepository';

export class SupabaseOnboardingRepository implements IOnboardingRepository {
  async getByToken(token: string): Promise<OnboardingFlow | null> {
    // Try magic_link_token first (new system)
    let { data, error } = await (supabase as any)
      .from('onboarding_flows')
      .select('*')
      .eq('magic_link_token', token)
      .maybeSingle();

    // Fallback to magic_token if not found
    if (!data && !error) {
      const result = await (supabase as any)
        .from('onboarding_flows')
        .select('*')
        .eq('magic_token', token)
        .maybeSingle();
      data = result.data;
      error = result.error;
    }

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    if (!data) return null;

    return this.mapToOnboardingFlow(data);
  }

  async getById(flowId: string): Promise<DetailedOnboardingFlow | null> {
    const { data, error } = await (supabase as any)
      .from('onboarding_flows')
      .select(`
        *,
        clients!onboarding_flows_client_id_fkey(name, credit_status)
      `)
      .eq('id', flowId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    // Get email logs for reminders
    const { data: emailLogs } = await (supabase as any)
      .from('email_logs')
      .select('*')
      .eq('onboarding_flow_id', flowId)
      .order('sent_at', { ascending: false });

    // Calculate missing roles using the centralized utility
    // This respects prepayment vs credit logic
    const roleStatus: OnboardingRoleStatus = {
      account_manager_completed: data.account_manager_completed,
      finance_manager_completed: data.finance_manager_completed,
      creative_manager_completed: data.creative_manager_completed,
      account_owner_completed: data.account_owner_completed,
    };
    
    const progressState = data.progress_state as Record<string, any> || {};
    const completionContext: OnboardingCompletionContext = {
      credit_status: (data.clients as any)?.credit_status,
      payment_method: progressState.paymentMethod || data.payment_method,
      has_credit_application: !!data.credit_application_id,
      credit_application_status: data.credit_application_status,
    };
    
    const missingRoles = getMissingRoles(roleStatus, completionContext);

    // Build steps
    const steps: OnboardingStep[] = [
      {
        role: 'Account Manager',
        completed: data.account_manager_completed,
        completed_at: data.account_manager_completed_at,
        completed_by: data.account_manager_user_id,
      },
      {
        role: 'Finance Manager',
        completed: data.finance_manager_completed,
        completed_at: data.finance_manager_completed_at,
        completed_by: data.finance_manager_user_id,
      },
      {
        role: 'Account Owner',
        completed: data.account_owner_completed,
        completed_at: data.account_owner_completed_at,
        completed_by: data.account_owner_user_id,
      },
      {
        role: 'Creator Manager',
        completed: data.creative_manager_completed,
        completed_at: data.creative_manager_completed_at,
        completed_by: data.creative_manager_user_id,
      },
    ];

    // Build activities from audit logs and email logs
    const activities: OnboardingActivity[] = [];

    // Add email activities
    (emailLogs || []).forEach((log: any) => {
      activities.push({
        id: log.id,
        flow_id: flowId,
        activity_type: 'reminder_sent',
        description: `Reminder email sent to ${log.recipient_email}`,
        performed_by: log.sent_by,
        created_at: log.sent_at,
        metadata: { template: log.template_name, status: log.status },
      });
    });

    // Sort activities by date
    activities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    const flow = this.mapToStalledOnboardingFlow(data, (data.clients as any)?.name || 'Unknown');

    // Fetch invitations
    const { data: invitations } = await (supabase as any)
      .from('user_invitations')
      .select('*')
      .eq('onboarding_flow_id', flowId);

    return {
      ...flow,
      steps,
      activities,
      invitations: invitations || [],
      // Board resolution fields
      board_resolution_required: data.board_resolution_required,
      board_resolution_uploaded: data.board_resolution_uploaded,
      board_resolution_file_url: data.board_resolution_file_url,
      board_resolution_verified_by: data.board_resolution_verified_by,
      board_resolution_verified_at: data.board_resolution_verified_at,
      board_resolution_verification_notes: data.board_resolution_verification_notes,
      director_name: data.director_name,
      director_id_number: data.director_id_number,
    };
  }

  async getAll(clientId: string): Promise<OnboardingFlow[]> {
    const { data, error } = await (supabase as any)
      .from('onboarding_flows')
      .select('*')
      .eq('client_id', clientId);

    if (error) throw error;

    return (data || []).map((flow: any) => this.mapToOnboardingFlow(flow));
  }

  async getStalledFlows(riskLevel?: 'low' | 'medium' | 'high' | 'critical'): Promise<StalledOnboardingFlow[]> {
    let query = (supabase as any)
      .from('onboarding_flows')
      .select(`
        *,
        clients!onboarding_flows_client_id_fkey(name)
      `)
      .neq('status', 'completed')
      .neq('status', 'cancelled');

    if (riskLevel) {
      query = query.eq('risk_level', riskLevel);
    }

    const { data, error } = await query.order('created_at', { ascending: true });

    if (error) throw error;

    return (data || []).map((flow: any) => 
      this.mapToStalledOnboardingFlow(flow, (flow.clients as any)?.name || 'Unknown')
    );
  }

  async createFlow(data: {
    client_id: string;
    created_by: string;
  }): Promise<OnboardingFlow> {
    const magicToken = crypto.randomUUID();

    // Fetch client email for primary contact
    const { data: client } = await (supabase as any)
      .from('clients')
      .select('primary_contact_email')
      .eq('id', data.client_id)
      .single();

    const { data: flow, error } = await (supabase as any)
      .from('onboarding_flows')
      .insert({
        client_id: data.client_id,
        magic_token: magicToken,
        primary_email: client?.primary_contact_email || '',
        status: 'pending',
        risk_level: 'low',
      })
      .select()
      .single();

    if (error) throw error;

    // Create SLA tracking for the new flow
    try {
      const clientData = await (supabase as any)
        .from('clients')
        .select('platform_tenant_id')
        .eq('id', data.client_id)
        .single();
      
      if (clientData.data?.platform_tenant_id) {
        await SlaMonitoringService.createOnboardingSla(
          flow.id,
          clientData.data.platform_tenant_id,
          data.client_id
        );
      }
    } catch (slaError) {
      console.error('[SupabaseOnboardingRepository] Failed to create SLA:', slaError);
      // Don't fail flow creation if SLA creation fails
    }

    return this.mapToOnboardingFlow(flow);
  }

  async updateProgress(flowId: string, data: {
    account_manager_completed?: boolean;
    finance_manager_completed?: boolean;
    creative_manager_completed?: boolean;
    account_owner_completed?: boolean;
  }): Promise<OnboardingFlow> {
    const updateData: any = {};
    const now = new Date().toISOString();

    if (data.account_manager_completed !== undefined) {
      updateData.account_manager_completed = data.account_manager_completed;
      if (data.account_manager_completed) {
        updateData.account_manager_completed_at = now;
      }
    }
    if (data.finance_manager_completed !== undefined) {
      updateData.finance_manager_completed = data.finance_manager_completed;
      if (data.finance_manager_completed) {
        updateData.finance_manager_completed_at = now;
      }
    }
    if (data.creative_manager_completed !== undefined) {
      updateData.creative_manager_completed = data.creative_manager_completed;
      if (data.creative_manager_completed) {
        updateData.creative_manager_completed_at = now;
      }
    }
    if (data.account_owner_completed !== undefined) {
      updateData.account_owner_completed = data.account_owner_completed;
      if (data.account_owner_completed) {
        updateData.account_owner_completed_at = now;
      }
    }

    const { data: flow, error } = await (supabase as any)
      .from('onboarding_flows')
      .update(updateData)
      .eq('id', flowId)
      .select()
      .single();

    if (error) throw error;

    return this.mapToOnboardingFlow(flow);
  }

  async completeRole(flowId: string, role: string, completed_by: string): Promise<void> {
    const roleField = `${role.toLowerCase().replace(' ', '_')}_completed`;
    const roleAtField = `${role.toLowerCase().replace(' ', '_')}_completed_at`;
    const roleUserField = `${role.toLowerCase().replace(' ', '_')}_user_id`;

    const { error } = await (supabase as any)
      .from('onboarding_flows')
      .update({
        [roleField]: true,
        [roleAtField]: new Date().toISOString(),
        [roleUserField]: completed_by,
      })
      .eq('id', flowId);

    if (error) throw error;
  }

  async cancelFlow(flowId: string, cancelled_by: string, reason: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('onboarding_flows')
      .update({
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancelled_by,
        cancellation_reason: reason,
      })
      .eq('id', flowId);

    if (error) throw error;
  }

  async inviteRoleMember(data: {
    flow_id: string;
    email: string;
    role: string;
    invited_by: string;
  }): Promise<OnboardingInvitation> {
    // Get the flow to get client_id
    const { data: flow } = await (supabase as any)
      .from('onboarding_flows')
      .select('client_id')
      .eq('id', data.flow_id)
      .single();

    if (!flow) throw new Error('Flow not found');

    // Create user invitation
    const token = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7);

    const { data: invitation, error } = await (supabase as any)
      .from('user_invitations')
      .insert({
        client_id: flow.client_id,
        email: data.email,
        role: data.role,
        invited_by: data.invited_by,
        token,
        expires_at: expiresAt.toISOString(),
        status: 'pending',
        metadata: { onboarding_flow_id: data.flow_id },
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: invitation.id,
      flow_id: data.flow_id,
      email: invitation.email,
      role: invitation.role,
      invited_by: invitation.invited_by,
      invited_at: invitation.invited_at,
      accepted: invitation.status === 'accepted',
      completed: false,
    };
  }

  async getFlowInvitations(flowId: string): Promise<OnboardingInvitation[]> {
    const { data, error } = await (supabase as any)
      .from('user_invitations')
      .select('*')
      .contains('metadata', { onboarding_flow_id: flowId });

    if (error) throw error;

    return (data || []).map((inv: any) => ({
      id: inv.id,
      flow_id: flowId,
      email: inv.email,
      role: inv.role,
      invited_by: inv.invited_by,
      invited_at: inv.invited_at,
      accepted: inv.status === 'accepted',
      completed: false,
    }));
  }

  async sendReminder(flowId: string, template: string, _sentBy?: string): Promise<void> {
    // 1. Fetch the flow to get recipient email and tenant context
    const { data: flow, error: flowErr } = await (supabase as any)
      .from('onboarding_flows')
      .select('id, primary_email, platform_tenant_id, client_id, progress_state, reminder_count, magic_link_token')
      .eq('id', flowId)
      .maybeSingle();

    if (flowErr) throw flowErr;
    if (!flow) throw new Error('Flow not found');

    // 2. Update reminder tracking
    const newCount = (flow.reminder_count || 0) + 1;
    const { error: updateErr } = await (supabase as any)
      .from('onboarding_flows')
      .update({
        last_reminder_sent_at: new Date().toISOString(),
        reminder_count: newCount,
      })
      .eq('id', flowId);

    if (updateErr) throw updateErr;

    // 3. Resolve the Hub tenant ID
    const { data: tenant } = await (supabase as any)
      .from('platform_tenants')
      .select('communications_hub_tenant_id, company_name')
      .eq('id', flow.platform_tenant_id)
      .maybeSingle();

    const hubTenantId = tenant?.communications_hub_tenant_id || flow.platform_tenant_id;
    const companyName = tenant?.company_name || 'Faces';
    const orgName = flow.progress_state?.preConfigured?.organization_name || companyName;

    // 4. Build the correct portal link using the magic_link_token
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://onboarding.faces.mt';
    const portalLink = flow.magic_link_token
      ? `${baseUrl}/onboarding?token=${flow.magic_link_token}`
      : `${baseUrl}/onboarding?flow=${flowId}`;

    // 5. Send via hub-notifications edge function
    await supabase.functions.invoke('hub-notifications', {
      body: {
        eventKey: 'onboarding.reminder.sent',
        correlationId: `flow_${flowId}_reminder_${newCount}`,
        recipients: [{
          email: flow.primary_email,
          firstName: flow.primary_email?.split('@')[0] || '',
        }],
        data: {
          ai_subject: `Reminder: Complete Your ${orgName} Onboarding`,
          ai_message: `This is a friendly reminder to complete your onboarding for ${orgName}. Your progress has been saved and you can continue where you left off.`,
          ai_tip: 'You can complete most steps in under 5 minutes.',
          company_name: orgName,
          portal_link: portalLink,
          recipient_name: flow.primary_email?.split('@')[0] || '',
          template_type: template,
          reminder_count: newCount,
        },
      },
      headers: {
        'x-tenant-id': hubTenantId,
      },
    });
  }

  async updateAdminNotes(flowId: string, notes: string, _updatedBy: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('onboarding_flows')
      .update({ admin_notes: notes })
      .eq('id', flowId);

    if (error) throw error;
  }

  async getAllFlows(platformTenantId?: string): Promise<OnboardingFlow[]> {
    let query = (supabase as any)
      .from('onboarding_flows')
      .select('*')
      .order('created_at', { ascending: false });

    if (platformTenantId) {
      query = query.eq('platform_tenant_id', platformTenantId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((d: any) => this.mapToOnboardingFlow(d));
  }

  async resetFlow(flowId: string, _resetBy: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('onboarding_flows')
      .update({
        status: 'pending',
        account_manager_completed: false,
        finance_manager_completed: false,
        creative_manager_completed: false,
        account_owner_completed: false,
        overall_progress: 0,
        admin_unlocked: false,
        reminder_count: 0,
        last_reminder_sent_at: null,
      })
      .eq('id', flowId);

    if (error) throw error;
  }

  async duplicateFlow(flowId: string, _createdBy: string): Promise<OnboardingFlow> {
    // Get the original flow
    const { data: original, error: fetchError } = await (supabase as any)
      .from('onboarding_flows')
      .select('*')
      .eq('id', flowId)
      .single();

    if (fetchError) throw fetchError;

    // Create a duplicate with reset state
    const { data: duplicated, error: insertError } = await (supabase as any)
      .from('onboarding_flows')
      .insert({
        client_id: original.client_id,
        platform_tenant_id: original.platform_tenant_id,
        magic_token: crypto.randomUUID(),
        primary_email: original.primary_email,
        status: 'pending',
        account_manager_completed: false,
        finance_manager_completed: false,
        creative_manager_completed: false,
        account_owner_completed: false,
        overall_progress: 0,
        admin_unlocked: false,
        reminder_count: 0,
        is_test_flow: true,
        created_via: 'admin_duplicate',
      })
      .select()
      .single();

    if (insertError) throw insertError;

    return this.mapToOnboardingFlow(duplicated);
  }

  private mapToOnboardingFlow(data: any): OnboardingFlow {
    const completed = [
      data.account_manager_completed,
      data.finance_manager_completed,
      data.account_owner_completed,
      data.creative_manager_completed,
    ].filter(Boolean).length;

    return {
      id: data.id,
      tenant_id: data.tenant_id,
      client_id: data.client_id || data.tenant_id,
      token: data.magic_token,
      magic_token: data.magic_token,
      primary_email: data.primary_email,
      status: data.status,
      flow_type: data.flow_type || 'client',
      created_at: data.created_at,
      completed_at: data.completed_at,
      account_manager_completed: data.account_manager_completed,
      finance_manager_completed: data.finance_manager_completed,
      creative_manager_completed: data.creative_manager_completed,
      account_owner_completed: data.account_owner_completed,
      overall_progress: (completed / 4) * 100,
      admin_unlocked: data.account_owner_completed,
      reminder_count: data.reminder_count || 0,
      last_reminder_sent_at: data.last_reminder_sent_at,
      reminders_sent: [],
      admin_notes: data.admin_notes,
      is_test_flow: data.is_test_flow || false,
      created_via: data.created_via || 'api',
    };
  }

  private mapToStalledOnboardingFlow(data: any, tenantName: string): StalledOnboardingFlow {
    const flow = this.mapToOnboardingFlow(data);
    
    const missingRoles = [];
    if (!data.account_manager_completed) missingRoles.push('Account Manager');
    if (!data.finance_manager_completed) missingRoles.push('Finance Manager');
    if (!data.account_owner_completed) missingRoles.push('Account Owner');
    if (!data.creative_manager_completed) missingRoles.push('Creator Manager');

    const daysStalled = Math.floor(
      (new Date().getTime() - new Date(data.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    return {
      ...flow,
      tenant_name: tenantName,
      account_manager_email: data.primary_email,
      days_stalled: daysStalled,
      last_activity: data.updated_at,
      missing_roles: missingRoles,
      risk_level: data.risk_level || 'low',
      notes: data.admin_notes,
    };
  }
}
